import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.#filterGallery();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const sku = detail.resource?.sku || this.#selectedVariantSku();
        const colour = this.#selectedColour();
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) {
          this.#filterGallery(sku, colour);
          return;
        }

        this.replaceWith(newMediaGallery);
        queueMicrotask(() => {
          const next = document.querySelector('media-gallery');
          if (next instanceof MediaGallery) next.filterGallery(sku, colour);
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  /**
   * Show gallery images for the selected colour (and SKU when present).
   * @param {string} [sku]
   * @param {string} [colour]
   */
  filterGallery(sku, colour) {
    this.#filterGallery(sku, colour);
  }

  /** @deprecated Use filterGallery */
  filterMediaBySku(sku) {
    this.#filterGallery(sku);
  }

  #pickerRoot() {
    return this.closest('.shopify-section') || document;
  }

  #variantPicker() {
    const root = this.#pickerRoot();
    return root.querySelector?.('variant-picker') || document.querySelector('variant-picker');
  }

  #selectedVariantSku() {
    const picker = this.#variantPicker();
    const script = picker?.querySelector('script[type="application/json"]:not(.vp-variant-map)');
    if (!script?.textContent) return '';

    try {
      return JSON.parse(script.textContent)?.sku || '';
    } catch {
      return '';
    }
  }

  #colourFieldset() {
    const picker = this.#variantPicker();
    if (!picker) return null;

    const fieldsets = [...picker.querySelectorAll('fieldset')];
    return (
      fieldsets.find((fieldset) => /colou?r/i.test(fieldset.querySelector('legend')?.textContent || '')) ||
      fieldsets.find((fieldset) => !/size/i.test(fieldset.querySelector('legend')?.textContent || '')) ||
      null
    );
  }

  #selectedColour() {
    const checked = this.#colourFieldset()?.querySelector('input:checked');
    return checked instanceof HTMLInputElement ? checked.value.trim() : '';
  }

  #colourNames() {
    const values = [
      ...new Set(
        [...(this.#colourFieldset()?.querySelectorAll('input[type="radio"]') || [])]
          .map((input) => (input instanceof HTMLInputElement ? input.value.trim() : ''))
          .filter(Boolean)
      ),
    ];
    values.sort((a, b) => b.length - a.length);
    return values;
  }

  /**
   * @param {string} alt
   */
  #altText(el) {
    if (!(el instanceof Element)) return '';
    return (
      el.querySelector('img')?.getAttribute('alt') ||
      el.getAttribute('alt') ||
      ''
    ).trim();
  }

  /**
   * Longest colour name that appears in the alt wins, so Pink Camo is not treated as Pink.
   * @param {string} alt
   * @param {string[]} colours
   */
  #longestColourInAlt(alt, colours) {
    const text = alt.toLowerCase();
    return colours.find((colour) => text.includes(colour.toLowerCase())) || '';
  }

  /**
   * @param {string} alt
   * @param {string} sku
   * @param {string} colour
   * @param {string[]} colours
   */
  #shouldShowMedia(alt, sku, colour, colours) {
    const text = alt.toLowerCase();
    const selectedColour = colour.trim();
    const selectedSku = sku.trim().toLowerCase();
    const colourHit = this.#longestColourInAlt(alt, colours);

    if (colourHit) {
      return Boolean(selectedColour) && colourHit.toLowerCase() === selectedColour.toLowerCase();
    }

    if (selectedSku) {
      if (text.includes(selectedSku)) return true;
      const skuTail = text.match(/\|\s*([a-z0-9]+)\s*$/i);
      if (skuTail && skuTail[1] !== selectedSku) return false;
    }

    return true;
  }

  /**
   * @param {Element} el
   * @param {boolean} show
   */
  #setHidden(el, show) {
    if (!(el instanceof HTMLElement)) return;
    el.hidden = !show;
    el.style.display = show ? '' : 'none';
  }

  /**
   * @param {string} [sku]
   * @param {string} [colour]
   */
  #filterGallery(sku, colour) {
    const selectedSku = String(sku ?? this.#selectedVariantSku() ?? '').trim();
    const skuPrefix = selectedSku.split('#')[0].trim();
    const selectedColour = String(colour ?? this.#selectedColour() ?? '').trim();
    const colours = this.#colourNames();

    if (!selectedColour && !selectedSku) return;

    const slides = [...this.querySelectorAll('slideshow-slide')];
    const thumbs = [...this.querySelectorAll('.slideshow-controls__thumbnail')];
    const gridMedia = [...this.querySelectorAll('.product-media')].filter(
      (el) => !el.closest('slideshow-slide, .slideshow-controls__thumbnail')
    );

    /** @param {Element} el */
    const showEl = (el) =>
      this.#shouldShowMedia(this.#altText(el), skuPrefix || selectedSku, selectedColour, colours);

    const slideShows = slides.map((el) => showEl(el));
    slides.forEach((el, index) => this.#setHidden(el, slideShows[index]));

    thumbs.forEach((el, index) => {
      const show = index < slideShows.length ? slideShows[index] : showEl(el);
      this.#setHidden(el, show);
    });

    gridMedia.forEach((el) => this.#setHidden(el, showEl(el)));

    const firstVisible = slideShows.findIndex(Boolean);
    if (firstVisible >= 0) this.slideshow?.select?.(firstVisible, undefined, { animate: false });
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
